import { i18n } from '@quadralize/i18n'
import { cn, getWeekdayShortLabel } from '@quadralize/utils'
import { AltArrowLeft, AltArrowRight } from '@solar-icons/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useMobile } from '../../../hooks/use-mobile'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../overlays/tooltip/tooltip'
import {
  AUTO_SCROLL_OFFSET,
  COL_MIN_W,
  DAYS_PER_WEEK,
  MINS_PER_HOUR,
  MONTH_PT,
  NOW_INTERVAL_MS,
  OCCUPANCY_HIGH,
  OCCUPANCY_MED,
  TIME_AXIS_W,
  TIME_LABEL_H,
} from './constants'
import { SchedulerDateTitle, SchedulerHeaderControls } from './header-controls'
import { MobileScheduler } from './mobile-scheduler'
import { MonthGrid } from './month-grid'
import { ResourceColumn } from './resource-column'
import type {
  BlockedSlot,
  SchedulerProps,
  SchedulerResource,
  SchedulerView,
  SchedulerVisibleRange,
} from './types'
import {
  calcOccupancy,
  endOfDay,
  formatDate,
  getMonthRange,
  getWeekDays,
  getWeekRange,
  hourToLabel,
  nowLabel,
  nowMin,
  startOfDay,
  toIso,
} from './utils'

// ─── Column abstraction (resource or weekday) ─────────────────────────────────

interface Column {
  id: string
  label: string
  subLabel?: string
  icon?: React.ReactNode
  isoDate?: string
  resource?: SchedulerResource
}

function getVisibleRange(activeDate: Date, view: SchedulerView): SchedulerVisibleRange {
  if (view === 'week') {
    const range = getWeekRange(activeDate)
    return {
      view,
      startDate: range.startDate,
      endDate: range.endDate,
    }
  }

  if (view === 'month') {
    const range = getMonthRange(activeDate)
    return {
      view,
      startDate: range.startDate,
      endDate: range.endDate,
    }
  }

  return {
    view,
    startDate: startOfDay(activeDate),
    endDate: endOfDay(activeDate),
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const Scheduler = React.forwardRef<HTMLDivElement, SchedulerProps>(
  (
    {
      className,
      date,
      view: controlledView,
      defaultView = 'day',
      showViewSwitcher = false,
      showCurrentTime = true,
      showOccupancyIndicators = true,
      showTodayButton = true,
      todayButtonLabel = 'Hoje',
      showDatePicker = true,
      datePickerLabel = 'Selecionar data',
      density = 'comfortable',
      showResourceTabs = true,
      activeResourceId,
      startTime = 7,
      endTime = 23,
      slotDuration = 30,
      hourHeight = 120,
      resources,
      events,
      blockedSlots = [],
      businessHours,
      isLoading = false,
      onSlotClick,
      onEventClick,
      onDateChange,
      onTodayClick,
      onDatePick,
      onViewChange,
      onRangeChange,
      onEventHover,
      onResourceClick,
      onBlockedSlotClick,
      onActiveResourceChange,
      renderHeaderExtras,
      renderEvent,
      renderSlot,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('ui', { i18n })
    const isCompactDensity = density === 'compact'
    // ── Mobile detection ─────────────────────────────────────────────────────
    const isMobile = useMobile()

    // Merge forwarded ref with local wrapperRef
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        wrapperRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      [ref],
    )

    // ── View state (controlled/uncontrolled) ────────────────────────────────
    const [internalView, setInternalView] = React.useState<SchedulerView>(
      controlledView ?? defaultView,
    )
    const isControlledView = controlledView !== undefined
    const view = controlledView ?? internalView

    const handleViewChange = React.useCallback(
      (nextView: SchedulerView) => {
        if (!isControlledView) {
          setInternalView(nextView)
        }
        onViewChange?.(nextView)
      },
      [isControlledView, onViewChange],
    )

    React.useEffect(() => {
      if (controlledView !== undefined) {
        setInternalView(controlledView)
      }
    }, [controlledView])

    // ── Active date (internal navigation state) ──────────────────────────────
    const [activeDate, setActiveDate] = React.useState(date)
    React.useEffect(() => {
      setActiveDate(date)
    }, [date])

    const handleDateChange = React.useCallback(
      (newDate: Date) => {
        setActiveDate(newDate)
        onDateChange?.(newDate)
      },
      [onDateChange],
    )

    const navigatePeriod = React.useCallback(
      (direction: -1 | 1) => {
        const nextDate = new Date(activeDate)

        if (view === 'week') {
          nextDate.setDate(nextDate.getDate() + direction * DAYS_PER_WEEK)
          handleDateChange(nextDate)
          return
        }

        if (view === 'month') {
          nextDate.setMonth(nextDate.getMonth() + direction)
          handleDateChange(nextDate)
          return
        }

        nextDate.setDate(nextDate.getDate() + direction)
        handleDateChange(nextDate)
      },
      [activeDate, handleDateChange, view],
    )

    const prevPeriod = React.useCallback(() => {
      navigatePeriod(-1)
    }, [navigatePeriod])

    const nextPeriod = React.useCallback(() => {
      navigatePeriod(1)
    }, [navigatePeriod])

    const goToToday = React.useCallback(() => {
      const todayDate = startOfDay(new Date())
      handleDateChange(todayDate)
      onTodayClick?.(todayDate, view)
    }, [handleDateChange, onTodayClick, view])

    React.useEffect(() => {
      onRangeChange?.(getVisibleRange(activeDate, view))
    }, [activeDate, onRangeChange, view])

    // ── Desktop-only derived values ──────────────────────────────────────────
    const totalHours = endTime - startTime
    const hours = Array.from({ length: totalHours }, (_, i) => startTime + i)
    const pixelsPerMinute = hourHeight / MINS_PER_HOUR
    const totalMinutes = totalHours * MINS_PER_HOUR
    const timeAxisW = TIME_AXIS_W
    const colMinWidth = COL_MIN_W

    // ── Live clock ──────────────────────────────────────────────────────────
    const [curMin, setCurMin] = React.useState(nowMin)
    const [curLabel, setCurLabel] = React.useState(nowLabel)
    React.useEffect(() => {
      if (!showCurrentTime) return

      const t = setInterval(() => {
        setCurMin(nowMin())
        setCurLabel(nowLabel())
      }, NOW_INTERVAL_MS)
      return () => clearInterval(t)
    }, [showCurrentTime])

    const today = new Date()
    const isToday = today.toDateString() === activeDate.toDateString()
    const isCurrentPeriod =
      view === 'day'
        ? isToday
        : view === 'week'
          ? (() => {
              const { startDate, endDate } = getWeekRange(activeDate)
              const todayAtMidnight = startOfDay(today).getTime()
              return todayAtMidnight >= startDate.getTime() && todayAtMidnight <= endDate.getTime()
            })()
          : activeDate.getMonth() === today.getMonth() &&
            activeDate.getFullYear() === today.getFullYear()
    const nowOffset = curMin - startTime * MINS_PER_HOUR
    const showNow =
      showCurrentTime &&
      view !== 'month' &&
      isToday &&
      nowOffset >= 0 &&
      nowOffset <= totalHours * MINS_PER_HOUR
    const nowTop = nowOffset * pixelsPerMinute

    // ── Auto-scroll to current time on mount ────────────────────────────────
    const bodyRef = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
      if (!showNow || !bodyRef.current) return
      const frame = requestAnimationFrame(() => {
        bodyRef.current?.scrollTo({
          top: Math.max(0, nowTop - AUTO_SCROLL_OFFSET),
          behavior: 'smooth',
        })
      })
      return () => cancelAnimationFrame(frame)
    }, [nowTop, showNow])

    // ── All columns (full list) ─────────────────────────────────────────────
    const allColumns: Column[] = React.useMemo(() => {
      if (view === 'week') {
        return getWeekDays(activeDate).map((d) => ({
          id: toIso(d),
          label: getWeekdayShortLabel(d.getDay()),
          subLabel: String(d.getDate()),
          isoDate: toIso(d),
        }))
      }

      if (view === 'month') {
        return []
      }

      const filtered = activeResourceId
        ? resources.filter((r) => r.id === activeResourceId)
        : resources
      return filtered.map((r) => ({ id: r.id, label: r.name, icon: r.icon, resource: r }))
    }, [view, activeDate, resources, activeResourceId])
    const selectedResourceId = activeResourceId ?? resources[0]?.id

    // ── Events / blocked slots per column ───────────────────────────────────
    const getColumnEvents = (col: Column): typeof events => {
      if (view === 'week') {
        const res = selectedResourceId
        return events.filter((e) => e.resourceId === res && e.date === col.isoDate)
      }

      const activeDateIso = toIso(activeDate)
      return events.filter((e) => e.resourceId === col.id && (!e.date || e.date === activeDateIso))
    }

    const getColumnBlocked = (col: Column): BlockedSlot[] => {
      if (view === 'week') {
        return blockedSlots.filter((slot) => {
          const matchesResource = !slot.resourceId || slot.resourceId === selectedResourceId
          if (!matchesResource) return false
          return !slot.date || slot.date === col.isoDate
        })
      }

      const activeDateIso = toIso(activeDate)
      return blockedSlots.filter((slot) => {
        const matchesResource = !slot.resourceId || slot.resourceId === col.id
        if (!matchesResource) return false
        return !slot.date || slot.date === activeDateIso
      })
    }

    // ── Header date label ───────────────────────────────────────────────────
    const headerDate =
      view === 'week'
        ? (() => {
            const days = getWeekDays(activeDate)
            const first = days[0]
            const last = days[DAYS_PER_WEEK - 1]
            if (!first || !last) return ''
            return `${first.getDate()} – ${last.getDate()} de ${MONTH_PT[last.getMonth()]}`
          })()
        : view === 'month'
          ? `${MONTH_PT[activeDate.getMonth()]} de ${activeDate.getFullYear()}`
          : formatDate(activeDate)

    const activeResource: SchedulerResource | undefined = selectedResourceId
      ? resources.find((r) => r.id === selectedResourceId)
      : undefined
    const headerExtras = renderHeaderExtras?.({
      view,
      activeDate,
      isMobile,
      resources,
      activeResourceId,
    })
    const headerControlButtonClass = cn(
      'rounded-lg font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-base',
      isCompactDensity ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
    )

    // ── Main Render ─────────────────────────────────────────────────────────
    return (
      <TooltipProvider delayDuration={300} disableHoverableContent>
        {isMobile && view !== 'month' ? (
          <div ref={mergedRef} className={cn('w-full h-full', className)} {...props}>
            <MobileScheduler
              date={activeDate}
              view={view}
              activeResourceId={activeResourceId}
              startTime={startTime}
              endTime={endTime}
              slotDuration={slotDuration}
              resources={resources}
              events={events}
              blockedSlots={blockedSlots}
              businessHours={businessHours}
              isLoading={isLoading}
              onSlotClick={onSlotClick}
              onEventClick={onEventClick}
              onDateChange={handleDateChange}
              onTodayClick={onTodayClick}
              onDatePick={onDatePick}
              onEventHover={onEventHover}
              onBlockedSlotClick={onBlockedSlotClick}
              onViewChange={handleViewChange}
              showViewSwitcher={showViewSwitcher}
              showCurrentTime={showCurrentTime}
              showOccupancyIndicators={showOccupancyIndicators}
              showTodayButton={showTodayButton}
              todayButtonLabel={todayButtonLabel}
              showDatePicker={showDatePicker}
              datePickerLabel={datePickerLabel}
              density={density}
              showResourceTabs={showResourceTabs}
              onActiveResourceChange={onActiveResourceChange}
              renderHeaderExtras={renderHeaderExtras}
              renderEvent={renderEvent}
              renderSlot={renderSlot}
              className="h-full"
            />
          </div>
        ) : (
          <div
            ref={mergedRef}
            className={cn(
              'relative flex flex-col w-full h-full rounded-xl border border-border-default bg-bg-base overflow-hidden',
              className,
            )}
            {...props}
          >
            {/* ── Date Header ── */}
            <div
              className={cn(
                'flex items-center gap-2 border-b border-border-default bg-bg-subtle/95 shrink-0 supports-backdrop-filter:backdrop-blur-md',
                isCompactDensity ? 'px-4 py-2.5' : 'px-5 py-3',
              )}
            >
              <div className="shrink-0 inline-flex items-center rounded-lg border border-border-subtle bg-bg-base/80">
                <button
                  type="button"
                  onClick={prevPeriod}
                  aria-label={
                    view === 'week'
                      ? t('scheduler.previousWeek')
                      : view === 'month'
                        ? t('scheduler.previousMonth')
                        : t('scheduler.previousDay')
                  }
                  className="p-1.5 rounded-l-lg text-text-tertiary transition-colors cursor-pointer hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
                >
                  <AltArrowLeft size={16} />
                </button>

                <span className="h-5 w-px bg-border-subtle" />

                <button
                  type="button"
                  onClick={nextPeriod}
                  aria-label={
                    view === 'week'
                      ? t('scheduler.nextWeek')
                      : view === 'month'
                        ? t('scheduler.nextMonth')
                        : t('scheduler.nextDay')
                  }
                  className="p-1.5 rounded-r-lg text-text-tertiary transition-colors cursor-pointer hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
                >
                  <AltArrowRight size={16} />
                </button>
              </div>

              <div className="min-w-0 flex-1">
                <SchedulerDateTitle
                  label={headerDate}
                  selectedDate={activeDate}
                  view={view}
                  showDatePicker={showDatePicker}
                  datePickerLabel={datePickerLabel}
                  triggerClassName={cn(
                    'max-w-full rounded-lg px-1.5 py-0.5 font-semibold capitalize text-text-primary transition-colors cursor-pointer hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                    isCompactDensity ? 'text-xs' : 'text-sm',
                  )}
                  textClassName={cn(
                    'font-semibold text-text-primary capitalize truncate',
                    isCompactDensity ? 'text-xs' : 'text-sm',
                  )}
                  onDateChange={handleDateChange}
                  onDatePick={onDatePick}
                />
                <div
                  className={cn(
                    'mt-0.5 flex items-center gap-2',
                    isCompactDensity ? 'text-[9px]' : 'text-[10px]',
                    showDatePicker && 'px-1.5',
                  )}
                >
                  {(view === 'week' || view === 'month') && activeResource && (
                    <div className="flex items-center gap-1 text-[10px] text-text-secondary">
                      {activeResource.icon && <span>{activeResource.icon}</span>}
                      <span>{activeResource.name}</span>
                    </div>
                  )}
                  {showCurrentTime && isToday && (
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-medium text-text-secondary tabular-nums">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                      </span>
                      <span>{curLabel}</span>
                    </div>
                  )}
                </div>
              </div>

              <SchedulerHeaderControls
                view={view}
                showViewSwitcher={showViewSwitcher}
                showTodayButton={showTodayButton}
                todayButtonLabel={todayButtonLabel}
                isCurrentPeriod={isCurrentPeriod}
                onTodayClick={goToToday}
                onViewChange={handleViewChange}
                density={density}
                buttonClassName={headerControlButtonClass}
                activeClassName="bg-primary/12 text-primary shadow-sm"
                inactiveClassName="text-text-tertiary hover:bg-bg-muted/80 hover:text-text-primary"
              />

              {headerExtras && <div className="ml-2 shrink-0">{headerExtras}</div>}
            </div>

            {/* ── Scrollable body ── */}
            <div ref={bodyRef} className="flex-1 overflow-auto relative min-h-0">
              {view === 'month' ? (
                <MonthGrid
                  date={activeDate}
                  resources={resources}
                  activeResourceId={activeResourceId}
                  events={events}
                  startTime={startTime}
                  isLoading={isLoading}
                  isMobile={isMobile}
                  onDateChange={handleDateChange}
                  onSlotClick={onSlotClick}
                  onEventClick={onEventClick}
                  onEventHover={onEventHover}
                  renderEvent={renderEvent}
                  renderSlot={renderSlot}
                  density={density}
                />
              ) : (
                <>
                  {/* Column headers — sticky top. Corner cell is also sticky-left */}
                  <div className="flex sticky top-0 z-30 border-b border-border-default bg-bg-subtle/95 supports-backdrop-filter:backdrop-blur-md">
                    {/* Corner cell — sticky on both axes */}
                    <div
                      className="shrink-0 sticky left-0 z-31 border-r border-border-default bg-bg-muted/95 flex items-center justify-center supports-backdrop-filter:backdrop-blur-md"
                      style={{ width: `${timeAxisW}px` }}
                    >
                      <span className="text-[10px] text-text-tertiary font-medium uppercase tracking-wider">
                        Hora
                      </span>
                    </div>

                    {/* Resource / Day headers */}
                    <div className="flex flex-1 min-w-0">
                      {allColumns.map((col) => {
                        const colEvents = getColumnEvents(col)
                        const occupancy = showOccupancyIndicators
                          ? calcOccupancy(colEvents, startTime, endTime)
                          : 0
                        const occColor =
                          occupancy >= OCCUPANCY_HIGH
                            ? 'bg-error'
                            : occupancy >= OCCUPANCY_MED
                              ? 'bg-warning'
                              : 'bg-success'
                        const isColToday = view === 'week' && col.isoDate === toIso(new Date())
                        const isClickableHeader =
                          view === 'day' && !!onResourceClick && !!col.resource

                        const colInner = (
                          <>
                            {/* Week view: day name + number stacked */}
                            {view === 'week' ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span
                                  className={cn(
                                    'text-[10px] font-medium uppercase tracking-wider',
                                    isColToday ? 'text-primary' : 'text-text-tertiary',
                                  )}
                                >
                                  {col.label}
                                </span>
                                <span
                                  className={cn(
                                    'flex items-center justify-center rounded-full font-bold tabular-nums text-base w-7 h-7',
                                    isColToday ? 'bg-primary text-white' : 'text-text-primary',
                                  )}
                                >
                                  {col.subLabel}
                                </span>
                              </div>
                            ) : (
                              /* Day view: icon + name */
                              <div className="flex items-center gap-1.5 w-full justify-center">
                                {col.icon && (
                                  <span className="text-primary shrink-0">{col.icon}</span>
                                )}
                                <span
                                  className={cn(
                                    'truncate font-semibold text-sm',
                                    isColToday ? 'text-primary' : 'text-text-primary',
                                  )}
                                >
                                  {col.label}
                                </span>
                                {isColToday && (
                                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                )}
                              </div>
                            )}

                            {showOccupancyIndicators && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-full h-1 rounded-full bg-border-subtle overflow-hidden cursor-default">
                                    <div
                                      className={cn(
                                        'h-full rounded-full transition-all duration-500',
                                        isLoading
                                          ? 'animate-pulse bg-bg-elevated w-full'
                                          : occColor,
                                      )}
                                      style={isLoading ? undefined : { width: `${occupancy}%` }}
                                    />
                                  </div>
                                </TooltipTrigger>
                                {!isLoading && (
                                  <TooltipContent side="bottom">
                                    <p>
                                      <strong>{occupancy}%</strong> da agenda ocupada
                                    </p>
                                    <TooltipArrow />
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )}
                          </>
                        )

                        const colClass = cn(
                          'flex-1 border-r border-border-default last:border-r-0',
                          'flex flex-col items-center justify-center gap-1',
                          'py-2 px-3 transition-colors',
                          isColToday && 'bg-primary/5',
                        )

                        const resource = col.resource
                        return isClickableHeader && resource ? (
                          <button
                            key={col.id}
                            type="button"
                            aria-label={`Ver ${col.label}`}
                            onClick={() => onResourceClick(resource)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onResourceClick(resource)
                              }
                            }}
                            className={cn(
                              colClass,
                              'cursor-pointer hover:bg-bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50',
                            )}
                            style={{ minWidth: `${colMinWidth}px` }}
                          >
                            {colInner}
                          </button>
                        ) : (
                          <div
                            key={col.id}
                            className={colClass}
                            style={{ minWidth: `${colMinWidth}px` }}
                          >
                            {colInner}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* ── Grid ── */}
                  <div className="flex">
                    {/* Time Axis — sticky left */}
                    <div
                      className="shrink-0 sticky left-0 z-20 bg-bg-base border-r border-border-default"
                      style={{ width: `${timeAxisW}px`, height: `${totalHours * hourHeight}px` }}
                    >
                      {hours.map((hour, i) => (
                        <div
                          key={hour}
                          className="absolute w-full flex items-start justify-end pr-1.5"
                          style={{ top: `${i * hourHeight}px`, height: `${hourHeight}px` }}
                        >
                          <span className="mt-1 text-[11px] text-text-tertiary font-medium tabular-nums bg-bg-base px-0.5 rounded-xs">
                            {hourToLabel(hour)}
                          </span>
                        </div>
                      ))}
                      {/* End-of-range label */}
                      <div
                        className="absolute w-full flex items-end justify-end pr-1.5 pb-1"
                        style={{
                          top: `${(totalHours - 1) * hourHeight + hourHeight - TIME_LABEL_H}px`,
                          height: `${TIME_LABEL_H}px`,
                        }}
                      >
                        <span className="text-[11px] text-text-tertiary font-medium tabular-nums bg-bg-base px-0.5 rounded-xs">
                          {hourToLabel(endTime)}
                        </span>
                      </div>
                    </div>

                    {/* Columns area */}
                    <div className="flex flex-1 relative min-w-0">
                      {/* Horizontal grid lines */}
                      <div className="absolute inset-0 pointer-events-none z-0">
                        {hours.map((hour, i) => (
                          <React.Fragment key={hour}>
                            <div
                              className="absolute left-0 right-0 border-t border-border-subtle"
                              style={{ top: `${i * hourHeight}px` }}
                            />
                          </React.Fragment>
                        ))}
                        {Array.from(
                          { length: Math.ceil(totalMinutes / slotDuration) - 1 },
                          (_, index) => (index + 1) * slotDuration,
                        )
                          .filter((offsetMinutes) => offsetMinutes % MINS_PER_HOUR !== 0)
                          .map((offsetMinutes) => (
                            <div
                              key={`slot-line-${offsetMinutes}`}
                              className="absolute left-0 right-0 border-t border-dashed border-border-subtle/50"
                              style={{ top: `${offsetMinutes * pixelsPerMinute}px` }}
                            />
                          ))}
                        <div
                          className="absolute left-0 right-0 border-t border-border-subtle"
                          style={{ top: `${totalHours * hourHeight}px` }}
                        />
                      </div>

                      {/* Now indicator */}
                      {showNow && (
                        <div
                          className="absolute left-0 right-0 z-30 pointer-events-none"
                          style={{ top: `${nowTop}px` }}
                        >
                          <div className="relative flex items-center">
                            <span className="relative flex h-3 w-3 shrink-0 -ml-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-50" />
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-error shadow shadow-error/30" />
                            </span>
                            <div className="flex-1 h-px bg-error/70" />
                          </div>
                        </div>
                      )}

                      {/* Resource / Day columns */}
                      {allColumns.map((col) => (
                        <ResourceColumn
                          key={col.id}
                          columnId={view === 'week' ? (selectedResourceId ?? col.id) : col.id}
                          view={view}
                          activeDate={activeDate}
                          resource={col.resource}
                          columnDate={col.isoDate}
                          events={getColumnEvents(col)}
                          blockedSlots={getColumnBlocked(col)}
                          businessHours={businessHours}
                          startTime={startTime}
                          endTime={endTime}
                          totalHours={totalHours}
                          hourHeight={hourHeight}
                          pixelsPerMinute={pixelsPerMinute}
                          slotDuration={slotDuration}
                          colMinWidth={colMinWidth}
                          isLoading={isLoading}
                          onSlotClick={onSlotClick}
                          onEventClick={onEventClick}
                          onEventHover={onEventHover}
                          onBlockedSlotClick={onBlockedSlotClick}
                          density={density}
                          renderEvent={renderEvent}
                          renderSlot={renderSlot}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </TooltipProvider>
    )
  },
)

Scheduler.displayName = 'Scheduler'
